/**
 * useResourceWebSocket Hook
 * 
 * React hook for subscribing to resource status updates via WebSocket.
 * Replaces polling-based resource status updates with real-time WebSocket notifications.
 * Automatically falls back to polling when WebSocket is unavailable.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { IVideoClip, IVideoClipResource, ResourceStatus } from '@packages/global';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { videoClipResourceApi } from '../services/user/videoClipResource.api';
import { TaskType, TaskStatus, MessageType, TaskStatusChangedMessage } from '../services/websocket/types';

/**
 * Mapping from TaskStatus (WebSocket) to ResourceStatus (resource map)
 * Requirements: 2.1 - Map TaskStatus to ResourceStatus for status updates
 */
const TASK_STATUS_TO_RESOURCE_STATUS: Record<TaskStatus, ResourceStatus> = {
  [TaskStatus.PENDING]: ResourceStatus.PENDING,
  [TaskStatus.SUBMITTED]: ResourceStatus.PENDING,
  [TaskStatus.PROCESSING]: ResourceStatus.GENERATING,
  [TaskStatus.COMPLETED]: ResourceStatus.COMPLETED,
  [TaskStatus.FAILED]: ResourceStatus.FAILED,
};

/**
 * Mapping from TaskType to the generation ID field name in IVideoClipResource
 * Used to match incoming WebSocket messages to resources
 */
const TASK_TYPE_TO_GENERATION_ID_FIELD: Record<TaskType, keyof IVideoClipResource | null> = {
  [TaskType.IMAGE_GENERATION]: 'imageGenerationId',
  [TaskType.VIDEO_GENERATION]: 'videoGenerationId',
  [TaskType.AUDIO_GENERATION]: 'audioGenerationId',
  [TaskType.VIDEO_LIPSYNC]: 'videoGenerationId', // Lip sync uses video generation
  [TaskType.IMAGE_EDIT]: 'imageEditId',
  [TaskType.EPISODE_SCRIPT]: null, // Not related to clip resources
  [TaskType.BATCH_RESOURCE]: null, // Handled separately
  [TaskType.VIDEO_EXPORT]: null, // Not related to clip resources
  [TaskType.ASSET_GENERATION]: null, // Asset generation is handled separately
  [TaskType.MUSIC_GENERATION]: null, // Music generation is handled separately
  [TaskType.MV_AUDIO_ANALYSIS]: null, // MV audio analysis is handled separately
  [TaskType.MUSIC_EDITING]: null, // Music editing is handled separately
  [TaskType.AUDIO_EDITING]: 'audioGenerationId', // Audio editing uses audioGenerationId
  [TaskType.AVATAR_TRAIN]: null, // Avatar train is not related to clip resources
  [TaskType.AVATAR_VIDEO]: null, // Avatar video is not related to clip resources
};

/**
 * Options for useResourceWebSocket hook
 */
export interface UseResourceWebSocketOptions {
  /** Polling interval for fallback mode (ms), default 3000 */
  pollingInterval?: number;
  /** Whether to enable the hook, default true */
  enabled?: boolean;
  /** Callback when resources complete generation */
  onResourceCompleted?: (clipIds: string[]) => void;
}

/**
 * Return type for useResourceWebSocket hook
 */
export interface UseResourceWebSocketReturn {
  /** All clip resource status Map<clipId, resources[]> */
  resourceStatusMap: Map<string, IVideoClipResource[]>;
  /** Set of clipIds with pending tasks */
  pendingClipIds: Set<string>;
  /** Whether currently polling (fallback mode) */
  isPolling: boolean;
  /** Whether WebSocket is connected */
  isWebSocketConnected: boolean;
  /** Manually trigger a poll/refresh */
  triggerPoll: () => Promise<void>;
  /** Get resources for a specific clip */
  getClipResources: (clipId: string) => IVideoClipResource[];
  /** Check if a clip has pending tasks */
  isClipPending: (clipId: string) => boolean;
}

// Note: ResourceWebSocketState interface removed - internal state is managed via useState hooks

/**
 * useResourceWebSocket hook
 * 
 * Subscribes to resource status updates via WebSocket for real-time notifications.
 * Automatically falls back to polling when WebSocket is unavailable.
 * 
 * @param videoClips - Array of video clips to monitor
 * @param options - Hook configuration options
 * @returns Resource status map, pending clip IDs, and helper functions
 */
export function useResourceWebSocket(
  videoClips: IVideoClip[],
  options: UseResourceWebSocketOptions = {}
): UseResourceWebSocketReturn {
  const {
    /** Polling interval for fallback mode (ms), default 3000 */
    pollingInterval = 3000,
    enabled = true,
    onResourceCompleted
  } = options;

  // Get WebSocket context
  const { client, isConnected, isFallbackMode: wsClientFallbackMode, reconnectAttempt } = useWebSocketContext();

  // State for resource status map
  const [resourceStatusMap, setResourceStatusMap] = useState<Map<string, IVideoClipResource[]>>(
    new Map()
  );

  // State for pending clip IDs
  const [pendingClipIds, setPendingClipIds] = useState<Set<string>>(new Set());

  // State for polling status (manual poll in progress)
  const [isPolling, setIsPolling] = useState(false);

  /**
   * Maximum reconnect attempts before forcing fallback mode
   * Requirements: 4.4 - Switch to fallback polling mode after 3 failed attempts
   */
  const MAX_RECONNECT_ATTEMPTS = 3;

  /**
   * State for fallback mode detection
   * Requirements: 4.1 - Activate polling when WebSocket enters fallback mode or disconnects
   * Requirements: 4.4 - Switch to fallback polling mode after 3 failed attempts
   * 
   * Fallback mode is activated when:
   * 1. WebSocket client is not connected (isConnected === false)
   * 2. WebSocket client enters fallback mode (isFallbackMode === true)
   * 3. Reconnect attempts reach the threshold (reconnectAttempt >= 3)
   * 
   * This state is reactive - when connection status changes, fallback mode updates accordingly
   */
  const [isFallbackMode, setIsFallbackMode] = useState<boolean>(() => {
    // Initial state: fallback mode if not connected, WebSocket client is in fallback mode,
    // or reconnect attempts have reached the threshold
    return !isConnected || wsClientFallbackMode || reconnectAttempt >= MAX_RECONNECT_ATTEMPTS;
  });

  // Ref for callback to avoid dependency changes causing re-subscriptions
  const onResourceCompletedRef = useRef(onResourceCompleted);
  onResourceCompletedRef.current = onResourceCompleted;

  // Ref for previous pending state (used for completion detection)
  const prevPendingClipIdsRef = useRef<Set<string>>(new Set());

  /**
   * Effect: Detect and update fallback mode based on WebSocket connection status
   * Requirements: 4.1 - Activate polling when WebSocket enters fallback mode or disconnects
   * Requirements: 4.4 - Switch to fallback polling mode after 3 failed attempts
   * 
   * This effect reactively updates the isFallbackMode state when:
   * 1. WebSocket connection status changes (isConnected)
   * 2. WebSocket client enters/exits fallback mode (wsClientFallbackMode)
   * 3. Reconnect attempts reach the threshold (reconnectAttempt >= 3)
   * 
   * Fallback mode is activated when:
   * - WebSocket is not connected (isConnected === false)
   * - WebSocket client is in fallback mode (wsClientFallbackMode === true)
   * - Reconnect attempts have reached the threshold (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS)
   * 
   * Fallback mode is deactivated when:
   * - WebSocket reconnects successfully (isConnected === true AND reconnectAttempt resets to 0)
   */
  useEffect(() => {
    // Determine if we should be in fallback mode
    // Fallback mode is active when:
    // 1. WebSocket is not connected, OR
    // 2. WebSocket client is in fallback mode, OR
    // 3. Reconnect attempts have reached the threshold (3 failed attempts)
    const shouldBeFallbackMode = !isConnected || wsClientFallbackMode || reconnectAttempt >= MAX_RECONNECT_ATTEMPTS;

    // Update state if it changed
    setIsFallbackMode(prevFallbackMode => {
      if (prevFallbackMode !== shouldBeFallbackMode) {
        console.debug('[useResourceWebSocket] Fallback mode changed', {
          previousMode: prevFallbackMode,
          newMode: shouldBeFallbackMode,
          isConnected,
          wsClientFallbackMode,
          reconnectAttempt,
          maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS
        });
      }
      return shouldBeFallbackMode;
    });
  }, [isConnected, wsClientFallbackMode, reconnectAttempt]);

  /**
   * Calculate pending clip IDs from resource status map
   * A clip is pending if at least one resource has status PENDING or GENERATING
   * @param statusMap - The resource status map to calculate from
   * @returns Set of clip IDs with pending tasks
   */
  const calculatePendingClipIds = useCallback((statusMap: Map<string, IVideoClipResource[]>): Set<string> => {
    const pendingIds = new Set<string>();

    statusMap.forEach((resources, clipId) => {
      const hasPending = resources.some(
        r => r.status === ResourceStatus.PENDING || r.status === ResourceStatus.GENERATING
      );
      if (hasPending) {
        pendingIds.add(clipId);
      }
    });

    return pendingIds;
  }, []);

  /**
   * Fetch all resource status for the given video clips
   * Populates resourceStatusMap and calculates pendingClipIds
   */
  const fetchAllResourceStatus = useCallback(async (): Promise<void> => {
    if (videoClips.length === 0) {
      setResourceStatusMap(new Map());
      setPendingClipIds(new Set());
      return;
    }

    try {
      const clipIds = videoClips.map(c => c.id);
      const result = await videoClipResourceApi.getBatchResourcesStatus(clipIds);

      // Convert API response to Map
      const newStatusMap = new Map<string, IVideoClipResource[]>();
      Object.entries(result).forEach(([clipId, resources]) => {
        newStatusMap.set(clipId, resources as IVideoClipResource[]);
      });

      // Calculate pending clip IDs from the new status map
      const newPendingIds = calculatePendingClipIds(newStatusMap);

      // Detect which clips transitioned from pending to completed
      const completedClipIds: string[] = [];
      prevPendingClipIdsRef.current.forEach(clipId => {
        if (!newPendingIds.has(clipId)) {
          completedClipIds.push(clipId);
        }
      });

      // Update state
      setResourceStatusMap(newStatusMap);
      setPendingClipIds(newPendingIds);
      prevPendingClipIdsRef.current = newPendingIds;

      // Notify completed clips via callback
      if (completedClipIds.length > 0 && onResourceCompletedRef.current) {
        onResourceCompletedRef.current(completedClipIds);
      }
    } catch (error) {
      console.error('Failed to fetch batch resource status:', error);
    }
  }, [videoClips, calculatePendingClipIds]);

  /**
   * Manually trigger a poll/refresh of resource status
   */
  const triggerPoll = useCallback(async (): Promise<void> => {
    setIsPolling(true);
    await fetchAllResourceStatus();
    setIsPolling(false);
  }, [fetchAllResourceStatus]);

  /**
   * Effect: Implement polling logic for fallback mode
   * Requirements: 4.3 - Poll at the same interval as the original useResourcePolling hook (3 seconds)
   * 
   * This effect:
   * 1. Starts polling when isFallbackMode is true and there are pending clips
   * 2. Uses setInterval with the configured pollingInterval (default 3000ms)
   * 3. Calls fetchAllResourceStatus on each interval
   * 4. Clears the interval when:
   *    - isFallbackMode becomes false (WebSocket reconnects)
   *    - Component unmounts
   *    - Hook is disabled
   *    - No more pending clips (optimization)
   */
  useEffect(() => {
    // Skip if hook is disabled
    if (!enabled) {
      return;
    }

    // Skip if not in fallback mode - WebSocket is handling updates
    if (!isFallbackMode) {
      console.debug('[useResourceWebSocket] Not in fallback mode, skipping polling');
      return;
    }

    // Optimization: Only poll when there are pending clips
    // If no clips are pending, there's nothing to poll for
    if (pendingClipIds.size === 0) {
      console.debug('[useResourceWebSocket] No pending clips, skipping polling');
      return;
    }

    console.debug('[useResourceWebSocket] Starting fallback polling', {
      pollingInterval,
      pendingClipCount: pendingClipIds.size
    });

    // Start polling interval
    const intervalId = setInterval(() => {
      console.debug('[useResourceWebSocket] Polling resource status (fallback mode)');
      fetchAllResourceStatus();
    }, pollingInterval);

    // Cleanup: Clear interval when dependencies change or component unmounts
    return () => {
      console.debug('[useResourceWebSocket] Stopping fallback polling');
      clearInterval(intervalId);
    };
  }, [enabled, isFallbackMode, pendingClipIds.size, pollingInterval, fetchAllResourceStatus]);

  /**
   * Get resources for a specific clip
   * @param clipId - The clip ID to get resources for
   * @returns Array of resources for the clip, or empty array if not found
   */
  const getClipResources = useCallback(
    (clipId: string): IVideoClipResource[] => {
      return resourceStatusMap.get(clipId) || [];
    },
    [resourceStatusMap]
  );

  /**
   * Check if a clip has pending tasks
   * @param clipId - The clip ID to check
   * @returns True if the clip has pending tasks
   */
  const isClipPending = useCallback(
    (clipId: string): boolean => {
      return pendingClipIds.has(clipId);
    },
    [pendingClipIds]
  );

  // Track previous videoClips for change detection
  const prevVideoClipsRef = useRef<IVideoClip[]>([]);

  // Track if initial fetch has been done (to handle enabled changing from false to true)
  const initialFetchDoneRef = useRef(false);

  /**
   * Effect: Fetch initial resource status and refresh when videoClips change
   * Requirements: 5.1, 5.2 - Initial data loading
   * Requirements: 1.4, 5.3 - Re-subscription/refresh on videoClips change
   * 
   * This effect handles:
   * 1. Initial data loading when the hook first mounts or when enabled becomes true
   * 2. Refreshing resourceStatusMap when videoClips array changes (new clips added/removed)
   * 3. Clearing state when all clips are removed
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Detect if videoClips actually changed (not just reference)
    const prevClipIds = new Set(prevVideoClipsRef.current.map(c => c.id));
    const currentClipIds = new Set(videoClips.map(c => c.id));

    const hasNewClips = videoClips.some(c => !prevClipIds.has(c.id));
    const hasRemovedClips = prevVideoClipsRef.current.some(c => !currentClipIds.has(c.id));
    const isInitialLoad = prevVideoClipsRef.current.length === 0 && videoClips.length > 0;

    // Also trigger fetch if this is the first time enabled becomes true with existing clips
    const isFirstEnabledWithClips = !initialFetchDoneRef.current && videoClips.length > 0;

    // Update ref for next comparison
    prevVideoClipsRef.current = videoClips;

    if (videoClips.length > 0) {
      // Fetch/refresh resource status when:
      // 1. Initial load (first time we have clips)
      // 2. New clips were added
      // 3. Clips were removed (to clean up stale data)
      // 4. First time enabled becomes true with existing clips
      if (isInitialLoad || hasNewClips || hasRemovedClips || isFirstEnabledWithClips) {
        console.debug('[useResourceWebSocket] VideoClips changed, refreshing resource status', {
          isInitialLoad,
          hasNewClips,
          hasRemovedClips,
          isFirstEnabledWithClips,
          clipCount: videoClips.length
        });
        fetchAllResourceStatus();
        initialFetchDoneRef.current = true;
      }
    } else {
      // Clear state when no video clips
      setResourceStatusMap(new Map());
      setPendingClipIds(new Set());
    }
  }, [enabled, videoClips, fetchAllResourceStatus]);

  // Reset initialFetchDoneRef when enabled becomes false
  useEffect(() => {
    if (!enabled) {
      initialFetchDoneRef.current = false;
    }
  }, [enabled]);

  /**
   * Find the clip ID that contains a resource matching the given taskId and taskType
   * @param taskId - The task ID to search for
   * @param taskType - The task type to determine which field to search
   * @param statusMap - The resource status map to search in
   * @returns Object with clipId and resourceIndex, or null if not found
   */
  const findClipByTaskId = useCallback((
    taskId: string,
    taskType: TaskType,
    statusMap: Map<string, IVideoClipResource[]>
  ): { clipId: string; resourceIndex: number } | null => {
    const generationIdField = TASK_TYPE_TO_GENERATION_ID_FIELD[taskType];
    if (!generationIdField) {
      return null;
    }

    for (const [clipId, resources] of statusMap.entries()) {
      const resourceIndex = resources.findIndex(
        r => r[generationIdField] === taskId
      );
      if (resourceIndex !== -1) {
        return { clipId, resourceIndex };
      }
    }

    return null;
  }, []);

  // Ref for debounced refresh when resource not found
  const pendingRefreshRef = useRef<NodeJS.Timeout | null>(null);
  const REFRESH_DEBOUNCE_MS = 500; // Debounce refresh to avoid too many API calls

  /**
   * Schedule a debounced refresh of resource status
   * Used when WebSocket message arrives but resource is not found in current map
   */
  const scheduleRefresh = useCallback(() => {
    // Clear any pending refresh
    if (pendingRefreshRef.current) {
      clearTimeout(pendingRefreshRef.current);
    }

    // Schedule a new refresh
    pendingRefreshRef.current = setTimeout(() => {
      console.debug('[useResourceWebSocket] Triggering refresh due to unmatched WebSocket message');
      fetchAllResourceStatus();
      pendingRefreshRef.current = null;
    }, REFRESH_DEBOUNCE_MS);
  }, [fetchAllResourceStatus]);

  /**
   * Effect: Register TASK_STATUS_CHANGED message handler
   * Requirements: 2.1 - Update resource status when task status changes
   * 
   * This handler:
   * 1. Maps TaskStatus to ResourceStatus using TASK_STATUS_TO_RESOURCE_STATUS
   * 2. Finds the matching resource by taskId and taskType
   * 3. Updates the resourceStatusMap for the matching clip
   * 4. Recalculates pendingClipIds
   * 5. If no matching resource found, schedules a refresh to fetch latest data
   */
  useEffect(() => {
    // Skip if hook is disabled or no WebSocket client available
    if (!enabled || !client) {
      return;
    }

    /**
     * Handle TASK_STATUS_CHANGED message
     * Updates the resource status in the map when a task status changes
     */
    const handleTaskStatusChanged = (message: TaskStatusChangedMessage): void => {
      const { taskId, taskType, newStatus } = message;

      // Get the generation ID field name for this task type
      const generationIdField = TASK_TYPE_TO_GENERATION_ID_FIELD[taskType];
      if (!generationIdField) {
        // This task type is not related to clip resources, ignore
        console.debug(`[useResourceWebSocket] Ignoring status change for non-resource task type: ${taskType}`);
        return;
      }

      // Map TaskStatus to ResourceStatus
      const resourceStatus = TASK_STATUS_TO_RESOURCE_STATUS[newStatus];
      if (resourceStatus === undefined) {
        console.warn(`[useResourceWebSocket] Unknown task status: ${newStatus}`);
        return;
      }

      // Find the matching clip and resource in the current resourceStatusMap
      setResourceStatusMap(prevMap => {
        let foundClipId: string | null = null;
        let foundResourceIndex: number = -1;

        // Search through all clips to find the matching resource
        for (const [clipId, resources] of prevMap.entries()) {
          const resourceIndex = resources.findIndex(
            r => r[generationIdField] === taskId
          );
          if (resourceIndex !== -1) {
            foundClipId = clipId;
            foundResourceIndex = resourceIndex;
            break;
          }
        }

        // If no matching resource found, schedule a refresh to get latest data
        // This handles the case where a new resource was created but not yet in our map
        if (!foundClipId || foundResourceIndex === -1) {
          console.debug(`[useResourceWebSocket] No matching resource found for taskId: ${taskId}, scheduling refresh`);
          scheduleRefresh();
          return prevMap;
        }

        // Create a new map with the updated resource
        const newMap = new Map(prevMap);
        const clipResources = [...(newMap.get(foundClipId) || [])];

        // Update the resource status
        clipResources[foundResourceIndex] = {
          ...clipResources[foundResourceIndex],
          status: resourceStatus,
        };

        newMap.set(foundClipId, clipResources);

        console.debug(`[useResourceWebSocket] Updated resource status`, {
          clipId: foundClipId,
          taskId,
          taskType,
          oldStatus: message.oldStatus,
          newStatus,
          resourceStatus
        });

        return newMap;
      });

      // Recalculate pendingClipIds after the map update
      // We use a separate setState to ensure we're working with the latest map
      setResourceStatusMap(currentMap => {
        const newPendingIds = calculatePendingClipIds(currentMap);
        setPendingClipIds(newPendingIds);
        return currentMap;
      });
    };

    // Register the message handler
    const unsubscribe = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      handleTaskStatusChanged
    );

    console.debug('[useResourceWebSocket] Registered TASK_STATUS_CHANGED handler');

    // Cleanup: Unregister the message handler and clear pending refresh
    return () => {
      unsubscribe();
      if (pendingRefreshRef.current) {
        clearTimeout(pendingRefreshRef.current);
        pendingRefreshRef.current = null;
      }
      console.debug('[useResourceWebSocket] Unregistered TASK_STATUS_CHANGED handler');
    };
  }, [enabled, client, calculatePendingClipIds, scheduleRefresh]);

  /**
   * Effect: Register TASK_COMPLETED message handler
   * Requirements: 2.2 - Fetch complete resource data and update resourceStatusMap
   * 
   * This handler:
   * 1. Identifies the clip and resource from the message (using taskId and taskType)
   * 2. Fetches complete resource data via API (to get the result URL and other details)
   * 3. Updates resourceStatusMap with the full resource data
   * 4. Recalculates pendingClipIds
   * 5. Triggers onResourceCompleted callback for the completed clip
   */
  useEffect(() => {
    // Skip if hook is disabled or no WebSocket client available
    if (!enabled || !client) {
      return;
    }

    /**
     * Handle TASK_COMPLETED message
     * Fetches complete resource data and updates the resourceStatusMap
     */
    const handleTaskCompleted = async (message: {
      taskId: string;
      taskType: TaskType;
      result?: any;
    }): Promise<void> => {
      const { taskId, taskType } = message;

      console.debug(`[useResourceWebSocket] Received TASK_COMPLETED`, {
        taskId,
        taskType
      });

      // Get the generation ID field name for this task type
      const generationIdField = TASK_TYPE_TO_GENERATION_ID_FIELD[taskType];
      if (!generationIdField) {
        // This task type is not related to clip resources, ignore
        console.debug(`[useResourceWebSocket] Ignoring completed event for non-resource task type: ${taskType}`);
        return;
      }

      // Find the matching clip in the current resourceStatusMap
      // We need to access the current state, so we use a ref pattern
      let foundClipId: string | null = null;

      setResourceStatusMap(prevMap => {
        const result = findClipByTaskId(taskId, taskType, prevMap);
        if (result) {
          foundClipId = result.clipId;
        }
        return prevMap; // Don't modify yet, just find the clip
      });

      // If no matching clip found, schedule a refresh to get latest data
      // This handles the case where a new resource was created but not yet in our map
      if (!foundClipId) {
        console.debug(`[useResourceWebSocket] No matching clip found for completed taskId: ${taskId}, scheduling refresh`);
        scheduleRefresh();
        return;
      }

      try {
        // Fetch complete resource data via API
        // This gets the full resource data including resultUrl and other details
        console.debug(`[useResourceWebSocket] Fetching complete resource data for clip: ${foundClipId}`);
        const resources = await videoClipResourceApi.getResourcesStatus(foundClipId);

        // Update resourceStatusMap with the full resource data
        setResourceStatusMap(prevMap => {
          const newMap = new Map(prevMap);
          newMap.set(foundClipId!, resources);

          console.debug(`[useResourceWebSocket] Updated resourceStatusMap with complete data`, {
            clipId: foundClipId,
            resourceCount: resources.length
          });

          return newMap;
        });

        // Recalculate pendingClipIds
        setResourceStatusMap(currentMap => {
          const newPendingIds = calculatePendingClipIds(currentMap);

          // Detect completion: if this clip was previously pending and is now not pending
          const wasPending = prevPendingClipIdsRef.current.has(foundClipId!);
          const isNowPending = newPendingIds.has(foundClipId!);

          if (wasPending && !isNowPending) {
            // Trigger onResourceCompleted callback for this clip
            // Requirements: 6.1 - Invoke callback when resource transitions to completed
            if (onResourceCompletedRef.current) {
              console.debug(`[useResourceWebSocket] Triggering onResourceCompleted for clip: ${foundClipId}`);
              onResourceCompletedRef.current([foundClipId!]);
            }
          }

          // Update pending state
          setPendingClipIds(newPendingIds);
          prevPendingClipIdsRef.current = newPendingIds;

          return currentMap;
        });

      } catch (error) {
        console.error(`[useResourceWebSocket] Failed to fetch complete resource data for clip ${foundClipId}:`, error);

        // Even if API fetch fails, update the status to COMPLETED based on the WebSocket message
        // This ensures the UI reflects the completion even if we can't get full details
        setResourceStatusMap(prevMap => {
          const result = findClipByTaskId(taskId, taskType, prevMap);
          if (!result) {
            return prevMap;
          }

          const newMap = new Map(prevMap);
          const clipResources = [...(newMap.get(result.clipId) || [])];

          // Update the resource status to COMPLETED
          clipResources[result.resourceIndex] = {
            ...clipResources[result.resourceIndex],
            status: ResourceStatus.COMPLETED,
          };

          newMap.set(result.clipId, clipResources);
          return newMap;
        });

        // Recalculate pendingClipIds
        setResourceStatusMap(currentMap => {
          const newPendingIds = calculatePendingClipIds(currentMap);
          setPendingClipIds(newPendingIds);
          return currentMap;
        });
      }
    };

    // Register the message handler
    const unsubscribe = client.onMessage(
      MessageType.TASK_COMPLETED,
      handleTaskCompleted
    );

    console.debug('[useResourceWebSocket] Registered TASK_COMPLETED handler');

    // Cleanup: Unregister the message handler
    return () => {
      unsubscribe();
      console.debug('[useResourceWebSocket] Unregistered TASK_COMPLETED handler');
    };
  }, [enabled, client, calculatePendingClipIds, findClipByTaskId, scheduleRefresh]);

  /**
   * Effect: Register TASK_FAILED message handler
   * Requirements: 2.3 - Update resource status to failed and store error message
   * 
   * This handler:
   * 1. Identifies the clip and resource from the message (using taskId and taskType)
   * 2. Updates the resource status to FAILED
   * 3. Stores the error message in the resource (if available)
   * 4. Recalculates pendingClipIds
   * 5. If no matching resource found, schedules a refresh to fetch latest data
   */
  useEffect(() => {
    // Skip if hook is disabled or no WebSocket client available
    if (!enabled || !client) {
      return;
    }

    /**
     * Handle TASK_FAILED message
     * Updates the resource status to FAILED and stores the error message
     */
    const handleTaskFailed = (message: {
      taskId: string;
      taskType: TaskType;
      error?: string;
    }): void => {
      const { taskId, taskType, error } = message;

      console.debug(`[useResourceWebSocket] Received TASK_FAILED`, {
        taskId,
        taskType,
        error
      });

      // Get the generation ID field name for this task type
      const generationIdField = TASK_TYPE_TO_GENERATION_ID_FIELD[taskType];
      if (!generationIdField) {
        // This task type is not related to clip resources, ignore
        console.debug(`[useResourceWebSocket] Ignoring failed event for non-resource task type: ${taskType}`);
        return;
      }

      // Find the matching clip and resource, then update status to FAILED
      setResourceStatusMap(prevMap => {
        const result = findClipByTaskId(taskId, taskType, prevMap);

        // If no matching resource found, schedule a refresh to get latest data
        // This handles the case where a new resource was created but not yet in our map
        if (!result) {
          console.debug(`[useResourceWebSocket] No matching resource found for failed taskId: ${taskId}, scheduling refresh`);
          scheduleRefresh();
          return prevMap;
        }

        const { clipId, resourceIndex } = result;

        // Create a new map with the updated resource
        const newMap = new Map(prevMap);
        const clipResources = [...(newMap.get(clipId) || [])];

        // Update the resource status to FAILED and store error message
        clipResources[resourceIndex] = {
          ...clipResources[resourceIndex],
          status: ResourceStatus.FAILED,
          errorMessage: error || 'Task failed',
        };

        newMap.set(clipId, clipResources);

        console.debug(`[useResourceWebSocket] Updated resource status to FAILED`, {
          clipId,
          taskId,
          taskType,
          errorMessage: error
        });

        return newMap;
      });

      // Recalculate pendingClipIds after the map update
      setResourceStatusMap(currentMap => {
        const newPendingIds = calculatePendingClipIds(currentMap);
        setPendingClipIds(newPendingIds);
        prevPendingClipIdsRef.current = newPendingIds;
        return currentMap;
      });
    };

    // Register the message handler
    const unsubscribe = client.onMessage(
      MessageType.TASK_FAILED,
      handleTaskFailed
    );

    console.debug('[useResourceWebSocket] Registered TASK_FAILED handler');

    // Cleanup: Unregister the message handler
    return () => {
      unsubscribe();
      console.debug('[useResourceWebSocket] Unregistered TASK_FAILED handler');
    };
  }, [enabled, client, calculatePendingClipIds, findClipByTaskId, scheduleRefresh]);

  return {
    resourceStatusMap,
    pendingClipIds,
    isPolling: isPolling || isFallbackMode, // True when manually polling OR in fallback mode
    isWebSocketConnected: isConnected,
    triggerPoll,
    getClipResources,
    isClipPending
  };
}
